import React from 'react'
import MultipleChoiceQuestion from './questions/MultipleChoiceQuestion'
import StudentProducedResponse from './questions/StudentProducedResponse'
import { renderContent } from '../utils/contentRenderer'
import AlternativeSolutionSection from './AlternativeSolutionSection'

/**
 * Renders one schema-v2 embedded artifact: inline SVG/markup via the shared content pipeline (which
 * sanitizes + preserves SVG), or a data-URI/URL as an <img>. Renders nothing if the content is empty.
 */
const RenderedImage = ({ img }) => {
  if (!img?.content) return null
  const content = img.content
  // Only image data URIs and https URLs (a plain http URL is mixed-content-blocked behind HTTPS;
  // data:text/html / data:application/javascript must not be accepted).
  const isImgSrc =
    typeof content === 'string' &&
    (content.startsWith('data:image/') || content.startsWith('https://'))
  // Check isImgSrc first so a data:image/svg+xml URI renders via <img src> (safer) rather than being
  // injected as innerHTML. Raw inline SVG markup falls through to renderContent, which sanitizes it
  // via sanitizeQuestionHTML/sanitizeSvgString (strips script / on* / javascript: hrefs).
  if (isImgSrc) {
    return (
      <img
        src={content}
        alt={img.metadata?.visualType || 'figure'}
        className="max-w-full h-auto"
        onError={(e) => {
          e.currentTarget.style.display = 'none'
        }}
      />
    )
  }
  if (img.outputType === 'svg' || /<svg/i.test(content)) {
    return <div className="overflow-x-auto">{renderContent(content, 'html')}</div>
  }
  return null
}

const ChallengeQuestionRenderer = ({
  question,
  userAnswer,
  onAnswerChange,
  isMarked,
  timeSpent,
  challengeId,
  stageId,
  readOnly = false,
  showCorrectAnswer = false,
  correctAnswer = null
}) => {
  if (!question) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No question available</p>
      </div>
    )
  }

  const handleAnswerChange = (answer) => {
    // Don't allow changes in read-only mode
    if (readOnly) return
    if (onAnswerChange) {
      onAnswerChange(answer)
    }
  }

  const renderQuestion = () => {
    switch (question.question_type) {
      case 'mcq':
        return (
          <MultipleChoiceQuestion
            question={question}
            details={question}
            selectedAnswer={userAnswer}
            onAnswerSelect={handleAnswerChange}
            readOnly={readOnly}
            showCorrectAnswer={showCorrectAnswer}
            correctAnswer={correctAnswer || question.correct_answer}
          />
        )

      case 'spr':
        return (
          <StudentProducedResponse
            textAnswer={userAnswer}
            onTextAnswerChange={handleAnswerChange}
            readOnly={readOnly}
            showCorrectAnswer={showCorrectAnswer}
            correctAnswer={correctAnswer || question.correct_answer}
          />
        )

      default:
        return (
          <div className="text-center py-8">
            <p className="text-gray-500">Unsupported question type: {question.question_type}</p>
          </div>
        )
    }
  }

  return (
    <div className="space-y-6">
      {/* Question Header */}
      <div className="border-b pb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-primary-600">
            {question.difficulty === 'E' ? 'Easy' : question.difficulty === 'M' ? 'Medium' : 'Hard'}
          </span>
          <span className="text-sm text-gray-500">Score Band: {question.score_band}</span>
        </div>

        {question.skill && (
          <div className="text-sm text-gray-600">
            <span className="font-medium">Skill:</span> {question.skill.skill_name}
          </div>
        )}
      </div>

      {/* Question Content */}
      <div className="space-y-4">
        {question.context && (
          <div
            className={`prose max-w-none ${question.test_type === 'math' ? 'text-center' : 'text-justify'}`}
          >
            {renderContent(question.context, question.content_type)}
          </div>
        )}
        <div className="prose max-w-none text-justify font-bold">
          {renderContent(question.questionText, question.content_type)}
        </div>
      </div>

      {/* Question Component */}
      {renderQuestion()}

      {/* Question Footer - Show rationale in review mode. Enhances with schema-v2 fields when present
          (key strategy, alternative-solution badge, embedded images); falls back to the flat rationale. */}
      {readOnly &&
        (question.rationale ||
          question.explanation ||
          question.rationaleStructured?.explanation) && (
          <div className="mt-6 p-4 bg-gray-50 rounded-lg space-y-3">
            <div className="flex items-center justify-between gap-2">
              <h4 className="text-sm font-medium text-gray-900">Rationale</h4>
              {question.hasAlternativeSolution && (
                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 whitespace-nowrap">
                  Alternative solution available
                </span>
              )}
            </div>
            {question.rationaleStructured?.keyStrategy && (
              <div className="text-sm">
                <span className="font-semibold text-gray-900">Key strategy: </span>
                <span className="text-gray-700">{question.rationaleStructured.keyStrategy}</span>
              </div>
            )}
            <div className="text-sm text-gray-600">
              {renderContent(
                question.rationaleStructured?.explanation ||
                  question.rationale ||
                  question.explanation,
                question.content_type
              )}
            </div>
            {Array.isArray(question.renderedImages) && question.renderedImages.length > 0 && (
              <div className="space-y-2">
                {question.renderedImages.map((img, i) => (
                  <RenderedImage key={`${img.outputType || 'img'}-${i}`} img={img} />
                ))}
              </div>
            )}
            {/* Separate alternative-solution section (its own datapoint, not part of the rationale) */}
            {question.alternativeSolution && (
              <AlternativeSolutionSection
                alternativeSolution={question.alternativeSolution}
                type={question.alternativeSolutionType}
                contentType={question.content_type}
              />
            )}
          </div>
        )}
    </div>
  )
}

export default ChallengeQuestionRenderer
